import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trophy, Medal, Share2, Heart, MessageCircle, Send } from "lucide-react";
import BrickTitle from "@/components/landing/BrickTitle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GAME_ORDER = ["patch-the-leak", "whack-a-nail", "tighten-the-bolt", "breaker-memory", "tool-match", "stack-the-bricks"];

const SHARE_TEXT =
  "I just put my name on the WillFixIt leaderboard playing handyman games — think you can beat my score? Grab a hammer and prove it:";

export default function Leaderboard() {
  const [boards, setBoards] = useState(null);
  const [likes, setLikes] = useState(0);
  const [liked, setLiked] = useState(false);
  const [comments, setComments] = useState([]);
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const [boardsError, setBoardsError] = useState(false);

  useEffect(() => {
    let active = true;
    let latestRequest = 0;
    const refreshBoards = async () => {
      const request = ++latestRequest;
      try {
        const { data } = await axios.get(`${API}/scores/summary`);
        if (active && request === latestRequest) {
          setBoards(data.games);
          setBoardsError(false);
        }
      } catch {
        if (active && request === latestRequest) setBoardsError(true);
      }
    };
    const onHashChange = () => {
      if (window.location.hash === "#leaderboard") refreshBoards();
    };
    refreshBoards();
    window.addEventListener("wfi-score-saved", refreshBoards);
    window.addEventListener("hashchange", onHashChange);
    axios.get(`${API}/likes`).then((r) => setLikes(r.data.likes)).catch(() => {});
    axios.get(`${API}/comments`).then((r) => setComments(r.data.comments)).catch(() => {});
    setLiked(localStorage.getItem("wfi-liked") === "1");
    return () => {
      active = false;
      window.removeEventListener("wfi-score-saved", refreshBoards);
      window.removeEventListener("hashchange", onHashChange);
    };
  }, []);

  const share = async () => {
    const url = `${window.location.origin}/#leaderboard`;
    const payload = { title: "WillFixIt Leaderboard", text: SHARE_TEXT, url };
    try {
      if (navigator.share) {
        await navigator.share(payload);
      } else {
        await navigator.clipboard.writeText(`${SHARE_TEXT} ${url}`);
        toast.success("Challenge link copied — paste it to a friend anywhere.");
      }
    } catch (e) {
      // user dismissed the share sheet — no action needed
    }
  };

  const like = async () => {
    if (liked) return;
    setLiked(true);
    localStorage.setItem("wfi-liked", "1");
    setLikes((l) => l + 1);
    try {
      const r = await axios.post(`${API}/likes`);
      setLikes(r.data.likes);
    } catch (e) {
      // optimistic count stays
    }
  };

  const postComment = async (e) => {
    e.preventDefault();
    if (!name.trim() || !text.trim()) return;
    setPosting(true);
    try {
      const r = await axios.post(`${API}/comments`, { name: name.trim(), text: text.trim() });
      setComments((c) => [r.data, ...c]);
      setText("");
      toast.success("Comment posted — thanks for the love!");
    } catch (err) {
      toast.error("Could not post your comment. Try again.");
    } finally {
      setPosting(false);
    }
  };

  return (
    <section id="leaderboard" className="py-16 max-w-6xl mx-auto px-4 sm:px-6" data-testid="leaderboard-section">
      <div className="text-center max-w-2xl mx-auto mb-10">
        <span className="text-xs font-bold uppercase tracking-widest text-[#D4691E]">Hall of Fame</span>
        <h2 className="text-3xl font-bold mt-2"><BrickTitle text="The WillFixIt Leaderboard" /></h2>
        <p className="text-sm text-[#A89F94] mt-3">
          Top scores from neighbors who played the Toolbox Games. Think you can beat them? Play above, save your
          score, then challenge a friend to knock you off the board.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3 mt-5">
          <button
            type="button"
            onClick={share}
            data-testid="leaderboard-share-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#D4691E] hover:bg-[#A94E10] text-white text-xs font-bold transition-colors shadow-sm"
          >
            <Share2 className="w-4 h-4" /> Challenge a Friend
          </button>
          <button
            type="button"
            onClick={like}
            data-testid="leaderboard-like-btn"
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border text-xs font-bold transition-colors ${
              liked
                ? "bg-[#FFF3EA] border-[#D4691E] text-[#A94E10]"
                : "bg-white border-[#E6DCD3] text-[#544D45] hover:border-[#D4691E]"
            }`}
          >
            <Heart className={`w-4 h-4 ${liked ? "fill-current text-[#D4691E]" : ""}`} />
            <span data-testid="leaderboard-like-count">{likes}</span> {liked ? "Liked" : "Like"}
          </button>
        </div>
      </div>

      {boardsError && <p role="alert" data-testid="leaderboard-refresh-error" className="text-sm text-[#FFAE70] mb-4">Latest scores couldn't load. Please try again shortly.</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {GAME_ORDER.map((key) => {
          const board = boards?.[key];
          return (
            <div key={key} className="bg-[#141A1E] rounded-xl border border-[#3A4752] p-5 shadow-sm" data-testid={`leaderboard-${key}`}>
              <div className="flex items-center gap-2 mb-3">
                <Trophy className="w-4 h-4 text-[#D4691E]" />
                <span className="font-bold text-xs text-[#F1F5F9]">{board?.label || key}</span>
              </div>
              {board?.scores?.length ? (
                <ol className="space-y-2">
                  {board.scores.map((s, i) => (
                    <li key={i} data-testid={`leaderboard-${key}-rank-${i + 1}`} className="flex items-center justify-between gap-2 text-xs">
                      <span className="flex items-center gap-1.5 text-[#D8D2CA] font-semibold min-w-0 break-all">
                        <Medal className={`w-3.5 h-3.5 ${i === 0 ? "text-amber-500" : i === 1 ? "text-slate-400" : "text-[#BBA892]"}`} />
                        {s.name}
                      </span>
                      <span data-testid={`leaderboard-${key}-score-${i + 1}`} className="font-bold text-[#D4691E] shrink-0">{s.score}</span>
                    </li>
                  ))}
                </ol>
              ) : (
                <p data-testid={`leaderboard-${key}-status`} className="text-[11px] text-[#8F9BA6]">{!boards ? (boardsError ? "Scores unavailable." : "Loading scores…") : "No scores yet — be the first name on this board."}</p>
              )}
            </div>
          );
        })}
      </div>

      {/* Comments wall */}
      <div className="mt-10 max-w-2xl mx-auto" data-testid="leaderboard-comments">
        <div className="flex items-center gap-2 mb-4 justify-center">
          <MessageCircle className="w-4 h-4 text-[#D4691E]" />
          <h3 className="font-bold text-sm text-[#1E252B]">Neighbor Talk</h3>
        </div>

        <form onSubmit={postComment} className="bg-[#141A1E] rounded-xl border border-[#3A4752] p-4 shadow-sm mb-4" data-testid="comment-form">
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              maxLength={24}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              data-testid="comment-name-input"
              className="px-3 py-2 rounded-lg bg-[#232A31] border border-[#3A4752] text-white placeholder-[#64717C] text-xs focus:outline-none focus:border-[#D4691E] sm:w-40"
            />
            <input
              type="text"
              maxLength={280}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Talk smack about a score, or say hey to Will…"
              data-testid="comment-text-input"
              className="flex-1 min-w-0 px-3 py-2 rounded-lg bg-[#232A31] border border-[#3A4752] text-white placeholder-[#64717C] text-xs focus:outline-none focus:border-[#D4691E]"
            />
            <button
              type="submit"
              disabled={posting}
              data-testid="comment-submit-btn"
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-[#1E252B] hover:bg-[#D4691E] disabled:opacity-60 text-white text-xs font-bold transition-colors"
            >
              <Send className="w-3.5 h-3.5" /> Post
            </button>
          </div>
        </form>

        <div className="space-y-2.5">
          {comments.length === 0 && (
            <p className="text-center text-xs text-[#8F9BA6]" data-testid="comments-empty">
              No comments yet — first one to talk gets bragging rights.
            </p>
          )}
          {comments.map((c) => (
            <div key={c.id} className="bg-[#141A1E] rounded-lg border border-[#3A4752] px-4 py-3" data-testid={`comment-${c.id}`}>
              <span className="font-bold text-xs text-[#D4691E]">{c.name}</span>
              <p className="text-xs text-[#D8D2CA] mt-0.5 leading-relaxed break-words">{c.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
